import { countries, CountryCode } from "@redotech/locale/countries";
import { shopifyProvinces } from "@redotech/locale/provinces";
import { useHandler } from "@redotech/react-util/hook";
import { DeliveryLocationRule } from "@redotech/redo-conversion-core-common/checkouts/checkout-tree";
import { useTranslation } from "@redotech/redo-i18n";
import { Accordion } from "@redotech/redo-web/accordion";
import {
  CheckboxValue,
  RedoCheckbox,
} from "@redotech/redo-web/arbiter-components/checkbox/redo-checkbox";
import { Flag } from "@redotech/redo-web/arbiter-components/flag/flag";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import classNames from "classnames";
import { memo, useMemo, useState } from "react";
import * as styles from "./location-rule-selection-modal.module.css";

interface ProvinceInfo {
  province: string;
  provinceCode: string;
}

const provincesByCountry: Map<string, ProvinceInfo[]> = (() => {
  const map = new Map<string, ProvinceInfo[]>();
  for (const entry of shopifyProvinces) {
    let list = map.get(entry.countryCode);
    if (!list) {
      list = [];
      map.set(entry.countryCode, list);
    }
    list.push({ province: entry.province, provinceCode: entry.provinceCode });
  }
  return map;
})();

interface CountrySelection {
  selected: boolean;
  provinces: Set<string>;
}

/** Tracks which countries/provinces are claimed by other conditions. */
interface OtherZoneClaims {
  /** Countries fully claimed (all provinces or no-province country). */
  fullCountries: Set<CountryCode>;
  /** Per-country set of province codes claimed in other conditions. */
  provincesByCountry: Map<CountryCode, Set<string>>;
}

function buildOtherZoneClaims(
  otherConditionRules: DeliveryLocationRule[],
): OtherZoneClaims {
  const fullCountries = new Set<CountryCode>();
  const claimedProvinces = new Map<CountryCode, Set<string>>();

  for (const rule of otherConditionRules) {
    const countryProvs = provincesByCountry.get(rule.countryCode);
    if (!countryProvs || !rule.provinces || rule.provinces.length === 0) {
      // Entire country is claimed
      fullCountries.add(rule.countryCode);
    } else {
      let set = claimedProvinces.get(rule.countryCode);
      if (!set) {
        set = new Set();
        claimedProvinces.set(rule.countryCode, set);
      }
      for (const p of rule.provinces) {
        set.add(p);
      }
    }
  }

  return { fullCountries, provincesByCountry: claimedProvinces };
}

function initSelectionState(
  rules: DeliveryLocationRule[],
): Map<CountryCode, CountrySelection> {
  const map = new Map<CountryCode, CountrySelection>();
  for (const rule of rules) {
    const countryProvinces = provincesByCountry.get(rule.countryCode);
    if (rule.provinces && rule.provinces.length > 0) {
      map.set(rule.countryCode, {
        selected: true,
        provinces: new Set(rule.provinces),
      });
    } else if (countryProvinces) {
      map.set(rule.countryCode, {
        selected: true,
        provinces: new Set(countryProvinces.map((p) => p.provinceCode)),
      });
    } else {
      map.set(rule.countryCode, { selected: true, provinces: new Set() });
    }
  }
  return map;
}

function selectionToRules(
  selection: Map<CountryCode, CountrySelection>,
): DeliveryLocationRule[] {
  const rules: DeliveryLocationRule[] = [];
  for (const [countryCode, state] of selection) {
    if (!state.selected) {
      continue;
    }
    const countryProvinces = provincesByCountry.get(countryCode);
    if (
      !countryProvinces ||
      state.provinces.size === 0 ||
      state.provinces.size === countryProvinces.length
    ) {
      rules.push({ countryCode });
    } else {
      rules.push({ countryCode, provinces: [...state.provinces] });
    }
  }
  return rules;
}

export const LocationRuleSelectionModal = memo(
  function LocationRuleSelectionModal({
    selectedRules,
    otherConditionRules,
    onRulesSelected,
    onClose,
  }: {
    selectedRules: DeliveryLocationRule[];
    otherConditionRules: DeliveryLocationRule[];
    onRulesSelected: (rules: DeliveryLocationRule[]) => void;
    onClose: () => void;
  }) {
    const { t } = useTranslation();
    const [selection, setSelection] = useState(() =>
      initSelectionState(selectedRules),
    );

    const claims = useMemo(
      () => buildOtherZoneClaims(otherConditionRules),
      [otherConditionRules],
    );

    const handleSave = useHandler(() => {
      onRulesSelected(selectionToRules(selection));
    });

    const handleCountryToggle = useHandler((countryCode: CountryCode) => {
      setSelection((prev) => {
        const next = new Map(prev);
        const existing = next.get(countryCode);
        const countryProvinces = provincesByCountry.get(countryCode);
        if (existing?.selected) {
          next.set(countryCode, { selected: false, provinces: new Set() });
        } else if (countryProvinces) {
          // Select only unclaimed provinces
          const claimedProvs =
            claims.provincesByCountry.get(countryCode) ?? new Set();
          const available = countryProvinces
            .map((p) => p.provinceCode)
            .filter((p) => !claimedProvs.has(p));
          next.set(countryCode, {
            selected: available.length > 0,
            provinces: new Set(available),
          });
        } else {
          next.set(countryCode, { selected: true, provinces: new Set() });
        }
        return next;
      });
    });

    const handleProvinceToggle = useHandler(
      (countryCode: CountryCode, provinceCode: string) => {
        setSelection((prev) => {
          const next = new Map(prev);
          const existing = next.get(countryCode) ?? {
            selected: false,
            provinces: new Set<string>(),
          };
          const newProvinces = new Set(existing.provinces);
          if (newProvinces.has(provinceCode)) {
            newProvinces.delete(provinceCode);
          } else {
            newProvinces.add(provinceCode);
          }
          const selected = newProvinces.size > 0;
          next.set(countryCode, { selected, provinces: newProvinces });
          return next;
        });
      },
    );

    const hasSelection = useMemo(() => {
      for (const state of selection.values()) {
        if (state.selected) {
          return true;
        }
      }
      return false;
    }, [selection]);

    return (
      <RedoModal
        contentScrollable
        modalSize="lg"
        onModalCloseRequested={onClose}
        primaryButton={{
          text: t("Save"),
          onClick: handleSave,
          disabled: !hasSelection
            ? t("Must select at least one location")
            : undefined,
        }}
        secondaryButton={{ text: t("Cancel"), onClick: onClose }}
        title={t("Select Locations")}
      >
        <Flex className={styles.listContainer} dir="column" gap="none">
          {countries.map((country) => (
            <CountryAccordionRow
              claims={claims}
              countryCode={country.code}
              countryName={country.name}
              key={country.code}
              onCountryToggle={handleCountryToggle}
              onProvinceToggle={handleProvinceToggle}
              selection={selection.get(country.code)}
            />
          ))}
        </Flex>
      </RedoModal>
    );
  },
);

const CountryAccordionRow = memo(function CountryAccordionRow({
  countryCode,
  countryName,
  selection,
  claims,
  onCountryToggle,
  onProvinceToggle,
}: {
  countryCode: CountryCode;
  countryName: string;
  selection: CountrySelection | undefined;
  claims: OtherZoneClaims;
  onCountryToggle: (countryCode: CountryCode) => void;
  onProvinceToggle: (countryCode: CountryCode, provinceCode: string) => void;
}) {
  const { t } = useTranslation();
  const provinces = provincesByCountry.get(countryCode);
  const isSelected = selection?.selected ?? false;
  const selectedProvinceCount = selection?.provinces.size ?? 0;

  const isFullyClaimed = claims.fullCountries.has(countryCode);
  const claimedProvinces = claims.provincesByCountry.get(countryCode);
  const isCountryDisabled =
    isFullyClaimed ||
    (provinces !== undefined &&
      claimedProvinces !== undefined &&
      claimedProvinces.size >= provinces.length);

  const checkboxValue: CheckboxValue = isCountryDisabled
    ? true
    : !isSelected
      ? false
      : provinces &&
          selectedProvinceCount > 0 &&
          selectedProvinceCount < provinces.length
        ? "indeterminate"
        : true;

  const handleToggle = useHandler((e?: React.MouseEvent) => {
    e?.preventDefault();
    if (isCountryDisabled) {
      return;
    }
    onCountryToggle(countryCode);
  });

  const noop = useHandler(() => {});

  if (!provinces) {
    return (
      <Flex
        align="center"
        className={classNames(
          styles.countryRowStandalone,
          styles.countryRow,
          isCountryDisabled && styles.countryRowDisabled,
        )}
        gap="sm"
        onClick={isCountryDisabled ? undefined : handleToggle}
        px="lg"
        py="md"
      >
        <RedoCheckbox
          disabled={isCountryDisabled}
          setValue={noop}
          value={checkboxValue}
        />
        <Flag countryCode={countryCode} size="sm" />
        <Text fontSize="sm">{countryName}</Text>
        {isCountryDisabled && (
          <Flex grow={1} justify="flex-end">
            <Text
              className={styles.inAnotherZone}
              fontSize="xs"
              textColor="secondary"
            >
              {t("In another condition")}
            </Text>
          </Flex>
        )}
      </Flex>
    );
  }

  const availableProvinceCount = claimedProvinces
    ? provinces.length - claimedProvinces.size
    : provinces.length;

  const provinceLabel =
    selectedProvinceCount > 0
      ? `${selectedProvinceCount} of ${availableProvinceCount} provinces`
      : "";

  const preview = (
    <Flex
      align="center"
      className={classNames(
        styles.countryRow,
        isCountryDisabled && styles.countryRowDisabled,
      )}
      gap="sm"
      px="lg"
      py="md"
      w="full"
    >
      <Flex onClick={handleToggle}>
        <RedoCheckbox
          disabled={isCountryDisabled}
          setValue={noop}
          value={checkboxValue}
        />
      </Flex>
      <Flag countryCode={countryCode} size="sm" />
      <Text fontSize="sm">{countryName}</Text>
      <Flex grow={1} justify="flex-end">
        {isCountryDisabled ? (
          <Text
            className={styles.inAnotherZone}
            fontSize="xs"
            textColor="secondary"
          >
            {t("In another condition")}
          </Text>
        ) : provinceLabel ? (
          <Text
            className={styles.inAnotherZone}
            fontSize="xs"
            textColor="secondary"
          >
            {provinceLabel}
          </Text>
        ) : null}
      </Flex>
    </Flex>
  );

  return (
    <Accordion className={styles.accordionWrapper} preview={preview}>
      <Flex dir="column" gap="none" pl="3xl">
        {provinces.map((province) => {
          const isClaimed = !!(
            claimedProvinces?.has(province.provinceCode) || isFullyClaimed
          );
          return (
            <ProvinceCheckboxRow
              checked={selection?.provinces.has(province.provinceCode) ?? false}
              countryCode={countryCode}
              disabled={isClaimed}
              key={province.provinceCode}
              onProvinceToggle={onProvinceToggle}
              provinceCode={province.provinceCode}
              provinceName={province.province}
            />
          );
        })}
      </Flex>
    </Accordion>
  );
});

const ProvinceCheckboxRow = memo(function ProvinceCheckboxRow({
  countryCode,
  provinceCode,
  provinceName,
  checked,
  disabled,
  onProvinceToggle,
}: {
  countryCode: CountryCode;
  provinceCode: string;
  provinceName: string;
  checked: boolean;
  disabled: boolean;
  onProvinceToggle: (countryCode: CountryCode, provinceCode: string) => void;
}) {
  const { t } = useTranslation();
  const noop = useHandler(() => {});

  const handleToggle = useHandler((e?: React.MouseEvent) => {
    e?.preventDefault();
    if (disabled) {
      return;
    }
    onProvinceToggle(countryCode, provinceCode);
  });

  return (
    <Flex
      align="center"
      className={classNames(
        styles.provinceRow,
        disabled && styles.provinceRowDisabled,
      )}
      gap="sm"
      onClick={disabled ? undefined : handleToggle}
      px="lg"
      py="sm"
    >
      <RedoCheckbox
        disabled={disabled}
        setValue={noop}
        size="sm"
        value={checked || disabled}
      />
      <Text fontSize="sm">{provinceName}</Text>
      {disabled && (
        <Flex grow={1} justify="flex-end">
          <Text
            className={styles.inAnotherZone}
            fontSize="xs"
            textColor="secondary"
          >
            {t("In another condition")}
          </Text>
        </Flex>
      )}
    </Flex>
  );
});
